import React, { useState } from "react";
import { Copy, Check, ExternalLink, Mail, Shield, FileText } from "lucide-react";
import { Reveal } from "../components/motion";
import { PageHero } from "../components/shared";
import { img } from "../data/site";

/* Pagina Whistleblowing: canale riservato. Nessun tracciamento:
   la chat e' disattivata su questa pagina (vedi ChatGate in App.js). */
function CodeChip({ label, code, tid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };
  return (
    <div className="wb-code" data-testid={tid}>
      <div style={{ display: "grid", gap: 3 }}>
        <span style={{ fontSize: 13, color: "var(--muted)" }}>{label}</span>
        <strong style={{ fontSize: 18, letterSpacing: "0.02em" }}>{code}</strong>
      </div>
      <button type="button" className="btn ghost sm" onClick={copy} aria-label={`Copia ${label}`} data-testid={`${tid}-copy`}>
        {copied ? <Check size={15} color="#0c7a3e" /> : <Copy size={15} />}
        {copied ? "Copiato" : "Copia"}
      </button>
    </div>
  );
}

const SOGGETTI = [
  "lavoratori subordinati, anche a tempo determinato",
  "lavoratori autonomi e collaboratori",
  "liberi professionisti e consulenti che lavorano per l'azienda",
  "volontari e tirocinanti",
  "azionisti e persone in società partecipate",
  "persone con funzioni di amministrazione, direzione, controllo, vigilanza o rappresentanza",
];

const DOCUMENTI = [
  ["orion-whistleblowing-policy.pdf", "Whistleblowing Policy (versione 1/2026)"],
  ["orion-informativa-persone-segnalanti.pdf", "Informativa privacy per le persone segnalanti"],
  ["orion-informativa-persone-segnalate.pdf", "Informativa privacy per le persone segnalate"],
  ["orion-comunicazione-whistleblowing.pdf", "Comunicazione di servizio Whistleblowing"],
];

export default function Whistleblowing() {
  return (
    <>
      <PageHero
        kick="Whistleblowing"
        lines={["Canale riservato", "per segnalazioni."]}
        sub="Uno spazio dedicato per segnalare, anche in forma anonima, comportamenti illeciti o irregolari di cui si è venuti a conoscenza nel contesto lavorativo."
        ph={img("sede-san-cesareo-drone-1.jpg")}
        testid="whistleblowing-hero"
      />

      <section className="sec" data-testid="whistleblowing-page">
        <div className="wrap" style={{ maxWidth: 900 }}>
          <div style={{ color: "var(--muted)", display: "grid", gap: 40 }}>
            <Reveal>
              <div style={{ display: "grid", gap: 14 }}>
                <h2 className="h3" style={{ color: "var(--text)" }}>Cos'è il whistleblowing e chi può segnalare</h2>
                <p style={{ margin: 0 }}>
                  Il whistleblowing è la segnalazione di informazioni su condotte illecite, fraudolente o comunque contrarie
                  alle norme e ai valori dell'azienda, apprese in un contesto lavorativo. La legge (D.Lgs. 24/2023) tutela
                  chi segnala in buona fede: sono vietate ritorsioni di qualsiasi tipo e l'identità della persona segnalante
                  è protetta.
                </p>
                <p style={{ margin: 0 }}>Possono utilizzare il canale interno:</p>
                <ul style={{ margin: 0, paddingLeft: 22, display: "grid", gap: 8 }}>
                  {SOGGETTI.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <div style={{ display: "grid", gap: 14 }}>
                <h2 className="h3" style={{ color: "var(--text)" }}>Canale interno: ReBlowing</h2>
                <p style={{ margin: 0 }}>
                  Orion Security ha adottato il canale <strong>ReBlowing</strong>, gestito da un soggetto esterno e
                  indipendente, <strong>ReNorm S.r.l.</strong>, che garantisce riservatezza, neutralità e tracciabilità
                  delle segnalazioni. Nessuna segnalazione passa per i sistemi interni dell'azienda prima di essere
                  presa in carico dal soggetto designato.
                </p>
                <div>
                  <a
                    href="https://www.renorm.it/reblowing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn dark"
                    data-testid="wb-portal"
                  >
                    Vai al portale ReBlowing <ExternalLink size={17} />
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <div style={{ display: "grid", gap: 14 }}>
                <h2 className="h3" style={{ color: "var(--text)" }}>Codici di accesso</h2>
                <p style={{ margin: 0 }}>
                  I codici di accesso al portale sono gli stessi per tutti:
                </p>
                <div style={{ display: "grid", gap: 10 }}>
                  <CodeChip label="Codice 1" code="OrY221RBW" tid="wb-code-1" />
                  <CodeChip label="Codice 2" code="OrY221WHISTLEBLOWING" tid="wb-code-2" />
                </div>
                <ul style={{ margin: 0, paddingLeft: 22, display: "grid", gap: 8 }}>
                  <li>Non serve alcuna registrazione.</li>
                  <li>È possibile segnalare in forma anonima.</li>
                  <li>Si consiglia di utilizzare un indirizzo email personale e non quello di lavoro, per tutelare la riservatezza.</li>
                  <li>Ogni segnalazione riceve conferma di presa in carico entro <strong>7 giorni</strong>.</li>
                  <li>È possibile chiedere un incontro diretto (segnalazione orale) tramite il portale.</li>
                </ul>
                <p style={{ margin: 0, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }} data-testid="wb-designato">
                  <Mail size={17} /> In caso di perdita dei codici, scrivi al Designato Whistleblowing:{" "}
                  <a href="mailto:m.negri@security.it">m.negri@security.it</a>
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <div style={{ display: "grid", gap: 14 }} data-testid="wb-anac">
                <h2 className="h3" style={{ color: "var(--text)" }}>Canale esterno</h2>
                <p style={{ margin: 0 }}>
                  È sempre possibile presentare una segnalazione esterna all'Autorità Nazionale Anticorruzione (ANAC),
                  alle condizioni previste dalla legge, sul sito{" "}
                  <a href="https://www.anticorruzione.it" target="_blank" rel="noopener noreferrer">www.anticorruzione.it</a>.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <div style={{ display: "grid", gap: 14 }}>
                <h2 className="h3" style={{ color: "var(--text)" }}>Documenti</h2>
                <div style={{ display: "grid", gap: 10 }} data-testid="wb-documenti">
                  {DOCUMENTI.map(([file, titolo]) => (
                    <a
                      className="wb-doc"
                      key={file}
                      href={`/documenti/whistleblowing/${file}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="wb-doc"
                    >
                      <FileText size={18} aria-hidden="true" />
                      <strong>{titolo}</strong>
                      <span style={{ marginLeft: "auto", fontSize: 13, color: "var(--blue)", fontWeight: 700, whiteSpace: "nowrap" }}>PDF</span>
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <p style={{ margin: 0, fontSize: 14, display: "flex", gap: 8, alignItems: "center" }}>
                <Shield size={15} aria-hidden="true" /> Su questa pagina non vengono utilizzati strumenti di tracciamento.
              </p>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
